import { getGenderCodeValue } from "constants/genderCode";
import { useTranslation } from "react-i18next";
import useGetSessionType from "constants/sessionType";
import React from "react";
import { RankingFilter } from "constants/rankingFilter";

export function checkRankingFilter(rankingFilterId, filter, filterValue) {
    return rankingFilterId === filter;
}

const SESSION_TYPE_SEPARATOR = "|";

/**
 * Il filtro Sessione accetta più tipi di leg: il server li riceve come ordinali separati da "|" (es. "0|1|3").
 * Un valore singolo ("0") resta valido.
 * @param {String|Number|Array} value stringa del server, numero singolo o array di ordinali
 * @returns {Number[]} ordinali senza duplicati, in ordine crescente
 */
export function parseSessionTypes(value) {

    if (value === null || value === undefined || value === "")
        return [];

    const parts = Array.isArray(value) ? value : String(value).split(SESSION_TYPE_SEPARATOR);
    const ids = parts.map(p => (typeof p === "string" ? p.trim() : p)).filter(p => p !== "").map(Number);
    
    return [...new Set(ids.filter(Number.isInteger))].sort((a, b) => a - b);
}

/**
 * @param {Array} ids ordinali dei tipi di leg
 * @returns {String} formato del server, es. "0|1|3"
 */
export function formatSessionTypes(ids) {
    return parseSessionTypes(ids).join(SESSION_TYPE_SEPARATOR);
}

/**
 * @returns {String} nomi dei tipi di leg separati da virgola; per un ordinale sconosciuto mostra l'ordinale stesso
 */
export function sessionTypesToNames(value, sessionType) {
    return parseSessionTypes(value)
        .map(id => sessionType?.find(e => e.id === id)?.name ?? id)
        .join(", ");
}

export function getFilterValue(filterType, filterValue, customField, t, sessionType) {

    const find = (collection, key, target) => collection?.find(e => e[key] === target);

    if (!filterType) {
        return "";
    }
    if (filterType === 0) {
        return sessionTypesToNames(filterValue, sessionType)
    }
    if (filterType === 1) {
        return filterValue;
    }
    if (filterType === 2) {
        return t(getGenderCodeValue(filterValue));
    }
    if (filterType === 3) {
        return find(customField, 'id', filterValue)?.name;
    }
    if (filterType === 4) {
        const { sessionTypeCodes, customFieldId } = splitSessionTypeAndCustomField(filterValue);
        const sessionTypeName = sessionTypesToNames(sessionTypeCodes, sessionType);
        const customFieldName = find(customField, 'id', customFieldId)?.name;
        return `${sessionTypeName} / ${customFieldName}`;
    }
}



/**
 * @param {Object} deps
 * @param {Array} deps.customField - array di customField con id e name
 * @returns {Function} - funzione anonima di mapping per filterValue(filterType, filterValue)
 */
export function useRankingFilterValueMapper({ customField = [] }) {

    const { t } = useTranslation();
    const sessionType = useGetSessionType();
    const find = (collection, key, target) => collection?.find(e => e[key] === target);

    const mappingStrategiesByFilterType = React.useMemo(() => ({
        [RankingFilter.TYPE.id]: 
            (filterValue) => sessionTypesToNames(filterValue, sessionType),

        [RankingFilter.AGE.id]: (filterValue) => 
            filterValue,

        [RankingFilter.GENDER.id]: (filterValue) => 
            t(getGenderCodeValue(Number(filterValue))),

        [RankingFilter.JOLLY.id]: (filterValue) => 
            find(customField, 'id', Number(filterValue))?.name,
        
        [RankingFilter.TYPE_JOLLY.id]: (filterValue) => {
            const { sessionTypeCodes, customFieldId } = splitSessionTypeAndCustomField(filterValue);
            const sessionName = sessionTypesToNames(sessionTypeCodes, sessionType);
            const customName = find(customField, 'id', customFieldId)?.name;
            return `${sessionName} / ${customName}`;
        }
    }), [customField, sessionType, t]);

    /* ritorna funzione mapper basata sulle strategie definite per filterType */
    return React.useMemo(() => {
        return (filterType, filterValue) => {
            if (filterType == null || filterValue == null)
                return undefined;

            const mappingFunction = mappingStrategiesByFilterType[Number(filterType)];
            return mappingFunction ? mappingFunction(filterValue) : undefined;
        }
    }, [mappingStrategiesByFilterType]);
}

/**
 * Helper function per splittare filterValue quando filterType è = 4 (TYPE_JOLLY)
 * Che prevede prima del " - " gli ordinal dei sessionType (separati da "|", anche uno solo),
 * e dopo il " - " prevede l'id del customField
 * @param {String} filterValue
 * @returns {{sessionTypeCodes: Number[], customFieldId: Number}}
 */
export function splitSessionTypeAndCustomField(filterValue) {
    const split = String(filterValue).split(" - ");
    return { sessionTypeCodes: parseSessionTypes(split[0]), customFieldId: Number(split[1]) };
}

const isRequiredFilled = (v) => v !== null && v !== undefined && v !== "";
const err = (field, error) => ({ field, error });

export function useRankingFilterValidator() {
    return React.useMemo(() => {
        const validationStrategies = {
            [RankingFilter.TYPE.id]: (v) =>
                parseSessionTypes(v.filterValue).length > 0 ? null : err("filterValue", "mandatoryField"),

            [RankingFilter.GENDER.id]: (v) =>
                isRequiredFilled(v.filterValue) ? null : err("filterValue", "mandatoryField"),

            [RankingFilter.JOLLY.id]: (v) =>
                isRequiredFilled(v.filterValue) ? null : err("filterValue", "mandatoryField"),

            [RankingFilter.AGE.id]: (v) => {
                if (!isRequiredFilled(v.minAge)) return err("minAge", "mandatoryField");
                if (v.minAge < 1) return err("minAge", "invalidValue");
                if (!isRequiredFilled(v.maxAge)) return err("maxAge", "mandatoryField");
                if (v.maxAge < v.minAge) return err("maxAge", "maxAgeLessThanMinAge");
                return null;
            },

            [RankingFilter.TYPE_JOLLY.id]: (v) => {
                if (parseSessionTypes(v.minAge).length === 0) return err("minAge", "mandatoryField");
                if (!isRequiredFilled(v.maxAge)) return err("maxAge", "mandatoryField");
                return null;
            },
        };

        return (values) => {
            const filterType = values.filter;
            if (!isRequiredFilled(filterType)) return null;

            const validationFunction = validationStrategies[Number(filterType)];
            return validationFunction ? validationFunction(values) : null;
        };
    }, []);
}


const formatAgeFilterValue = (minAge, maxAge) => `${minAge} - ${maxAge}`;

export function useRankingFilterNormalizer() {
  return React.useMemo(() => {
    const normalizationStrategies = {
      [RankingFilter.TYPE.id]: (values) => ({
        ...values,
        filterValue: formatSessionTypes(values.filterValue),
      }),

      [RankingFilter.AGE.id]: (values) => {
        const copy = { ...values };
        copy.filterValue = formatAgeFilterValue(copy.minAge, copy.maxAge);
        delete copy.minAge;
        delete copy.maxAge;
        return copy;
      },

      [RankingFilter.TYPE_JOLLY.id]: (values) => {
        const copy = { ...values };
        copy.filterValue = formatAgeFilterValue(formatSessionTypes(copy.minAge), copy.maxAge);
        delete copy.minAge;
        delete copy.maxAge;
        return copy;
      },
    };

    return (values) => {
      const filterType = values.filter;
      if (!isRequiredFilled(filterType)) return values;

      const normalizationFunction = normalizationStrategies[Number(filterType)];
      return normalizationFunction ? normalizationFunction(values) : values;
    };
  }, []);
}